import React from 'react';

import { ArrayFieldItemTemplateProps, RJSFSchema } from '@rjsf/utils';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton } from '@mui/material';

/**
 * SimpleArrayFieldItemTemplate - A custom RJSF ArrayFieldItemTemplate that renders
 * array items in a horizontal layout with controls on the right.
 *
 * Features:
 * - Horizontal layout (fields grow to fill space, controls on the right)
 * - Move up/down and remove buttons
 * - Minimal styling without Card/Paper wrappers
 *
 * This is used with SimpleArrayFieldTemplate to achieve the desired layout
 * where array item fields are displayed side-by-side.
 */

export default function SimpleArrayFieldItemTemplate({
  children,
  disabled,
  readonly,
  buttonsProps,
}: ArrayFieldItemTemplateProps<unknown, RJSFSchema>) {
  const {
    hasMoveDown = false,
    hasMoveUp = false,
    hasRemove = false,
    onMoveDownItem,
    onMoveUpItem,
    onRemoveItem,
  } = buttonsProps ?? {};

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1,
        px: 0,
        borderRadius: 1,
      }}
    >
      {/* Array item fields - grow to fill available space */}
      <Box sx={{ flexGrow: 1 }}>{children}</Box>

      {/* Controls on the right */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {hasMoveUp && (
          <IconButton size="small" onClick={onMoveUpItem} disabled={disabled || readonly} aria-label="Move up">
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
        )}
        {hasMoveDown && (
          <IconButton size="small" onClick={onMoveDownItem} disabled={disabled || readonly} aria-label="Move down">
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        )}
        {hasRemove && (
          <IconButton
            size="small"
            onClick={onRemoveItem}
            disabled={disabled || readonly}
            color="error"
            aria-label="Remove"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
